/* eslint
    @typescript-eslint/no-shadow: "warn",
    @typescript-eslint/no-use-before-define: "warn",
    prefer-arrow-callback: "warn",
    no-empty: "warn",
    no-restricted-syntax: ["warn", "ForOfStatement"],
    prefer-const: "warn",
    prefer-destructuring: "warn",
    no-else-return: "warn",
    import/no-mutable-exports: "warn",
    no-unused-expressions: "warn",
    no-useless-return: "warn",
    object-shorthand: "warn",
    arrow-body-style: "warn"
*/

// example
// element('.about'); // <div class="about">
// element('[data-action="top-nav"]'); // <div class="nav" data-action="top-nav">
// export const element  = (query: string) => document.querySelector(query);
export const element = (query: string) =>
  document.querySelector<any>(query);

export const elements = (query: string) =>
  document.querySelectorAll<any>(query);

export const elementsOf = (targetElement: Element | HTMLElement, query: string) =>
  targetElement.querySelector<any>(query);

/**
 * Selects the first element that matches the given CSS selector,
 * optionally scoped to a specific context element.
 *
 * @template T - The expected element type, defaults to `Element`.
 * @param {string} selector - A valid CSS selector string.
 * @param {ParentNode} [context] - Optional context element to scope the search within.
 *                                 If omitted, the search is performed in the entire document.
 * @returns {(T | null)} The first matching element of type `T`, or `null` if no match is found.
 *
 * @example
 * // Search globally
 * const nav = getElement<HTMLElement>('.main-nav');
 *
 * @example
 * // Search within a specific container
 * const container = document.getElementById('sidebar');
 * const button = getElement<HTMLButtonElement>('.submit', container);
 */
export const getElement = <T extends Element = Element>(
  selector: string,
  context: ParentNode = document
): T | null =>
  context.querySelector<T>(selector);

/**
 * Selects all elements that match the given CSS selector,
 * optionally scoped to a specific context element.
 *
 * @template T - The expected element type, defaults to `Element`.
 * @param {string} selector - A valid CSS selector string.
 * @param {ParentNode} [context] - Optional context element to scope the search within.
 *                                 If omitted, the search is performed in the entire document.
 * @returns {NodeListOf<T>} A live static NodeList of matching elements of type `T`.
 *                          Returns an empty list if no matches are found.
 *
 * @example
 * // Get all section elements globally
 * const sections = getElements<HTMLElement>('section');
 *
 * @example
 * // Get all buttons within a specific container
 * const form = document.querySelector('form');
 * const buttons = getElements<HTMLButtonElement>('button', form);
 */
export const getElements = <T extends Element = Element>(
  selector: string,
  context: ParentNode = document
): NodeListOf<T> =>
  context.querySelectorAll<T>(selector);

/**
 * Reads the value of a cookie by name from `document.cookie`.
 *
 * @param {string} name - The name of the cookie to read.
 * @returns {(string | undefined)} The cookie's value, or `undefined` if no cookie
 *                                 with that name is set.
 *
 * @example
 * const sessionId = getCookie('e_sessionId');
 * if (sessionId) {
 *   // use sessionId
 * }
 */
export const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }
  return undefined;   // explicit — satisfies noImplicitReturns
};

// example
// elementContains(document.querySelector('head'), document.querySelector('title')); // true
// elementContains(document.querySelector('body'), document.querySelector('body')); // false
export const elementContains = (parent: Element, child: Element) => parent !== child && parent.contains(child);

export const on = (action: string | Element, type: string, callback: Function, ref?: object, meta?: object) => {
  docReady(() => {
    type.split(' ').forEach(function (t) {
      (action instanceof Element ? action : element(action)).addEventListener(
        t,
        (e) => {
          try {
            callback.call(e.target, e, ref ?? null, meta ?? null);
          } catch (err) { }
        },
        { once: false, passive: true }
      );
    });
  });
};

export const one = (action: string | Element, type: string, callback: Function) => {
  docReady(() => {
    type.split(' ').forEach(function (e) {
      (action instanceof Element ? action : element(action)).addEventListener(e, callback, { once: true });
    });
  });
};

export const event = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    element(`[data-event="${action}"]`)?.addEventListener(type, callback, false);
  });
};

export const events = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    elements(`[data-event="${action}"]`)?.forEach((item) => {
      item.addEventListener(type, callback, false);
    });
  });
};

export const action = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    element(`[data-action="${action}"]`)?.addEventListener(type, callback, false);
  });
};

export const actions = (action: string, type: string, callback: EventListener | EventListenerObject | Function) => {
  docReady(() => {
    elements(`[data-action="${action}"]`)?.forEach((el) => el?.addEventListener(type, callback, false));
  });
};

export const bind = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    elements(action || '')?.forEach((item) => {
      type.split(' ').forEach(t => {
        item.addEventListener(t, callback, false);
      });
    });
  });
};

export const off = (action: string | Element | HTMLElement | Array<string | Element | HTMLElement>, type: string, callback: EventListener | EventListenerObject) => {
  if (action instanceof String || action instanceof Element || action instanceof HTMLElement) {
    (action instanceof Element || action instanceof HTMLElement ? action : element(<string>action)).removeEventListener(type, callback, false);
  }

  if (action instanceof Array) {
    for (let x of action) {
      let item: any = x;
      (item instanceof Element || item instanceof HTMLElement ? item : element(<string>item)).removeEventListener(type, callback, false);
    }
  }
};

export const offAll = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  elements(action).forEach((item) => {
    item.removeEventListener(type, callback, false);
  });
};

export interface IEventChain {
  target: string;
  type: string;
  events: Array<IEventChainItem>;
}

export interface IEventChainItem {
  action?: string;
  event?: string;
  callback?: Function;
}

export const eventChain = (arg: IEventChain) => {
  bind(arg.target, arg.type, (e) => {
    const target: any = e.target;
    let eventTarget = target?.attributes['data-action']?.value ?? target?.attributes['data-event']?.value;

    if (!eventTarget) eventTarget = target.parentNode?.attributes['data-action']?.value ?? target.parentNode?.attributes['data-event']?.value;

    if (!eventTarget) return;

    const item: any = arg.events.find(({ action, event }) => (action ?? event).includes(eventTarget));
    if (item?.callback) item.callback(e);
  });
};

export const getBlazorEditMode = () => {
  if (!window.isAdminModeEnabled) {
    return false;
  }
  if (window.isEditModeEnabled !== undefined) {
    // Return value to avoid parsing cookies constantly
    return window.isEditModeEnabled;
  }
  const cookies = document.cookie.split(";");
  const editModeCookie = cookies.find(cookie => cookie.trim().startsWith("_AdminEditMode"));
  window.isEditModeEnabled = editModeCookie?.split("=")[1].toLowerCase() === "true";
  return window.isEditModeEnabled;
};


// Need to wait for blazor

export const docReady = (callback?: Function, context?: Object | string | number | boolean) => {

  let blazorFinishedLoadingEventRegistered: boolean = true;

  // Method to trigger window events
  if (!window.EasyfyMethods) {
    window.EasyfyMethods = {};
    window.EasyfyMethods.triggerWindowEvent = (eventName, eventData) => {
      if (eventName) {
        let event = new CustomEvent(eventName, {
          detail: eventData
        });
        window.dispatchEvent(event);
      }
    };
  }

  if (getBlazorEditMode()) {
    blazorFinishedLoadingEventRegistered = false;
  }

  // The public function name defaults to window.docReady
  // but you can pass in your own object and own function name and those will be used
  // if you want to put them in a different namespace
  // funcName = funcName || "docReady";
  // baseObj = baseObj || window;
  let readyList = [];
  let readyFired = false;
  let readyEventHandlersInstalled = false;

  // call this when the document is ready
  // this function protects itself against being called more than once
  const ready = () => {
    const readyBody = () => {
      if (!readyFired) {
        // this must be set to true before we start calling callbacks
        readyFired = true;
        for (let i = 0; i < readyList.length; i++) {
          // if a callback here happens to add new ready handlers,
          // the docReady() function will see that it already fired
          // and will schedule the callback to run right after
          // this event loop finishes so all handlers will still execute
          // in order and no new ones will be added to the readyList
          // while we are processing the list
          readyList[i].fn.call(window, readyList[i].ctx);
          // allow any closures held by these functions to free
        }
        readyList = [];
      }
    };
    if (getBlazorEditMode() && !window.blazorFinishedLoading) {
      if (!blazorFinishedLoadingEventRegistered) {
        blazorFinishedLoadingEventRegistered = true;
        window.addEventListener('blazorFinishedLoading', () => {
          window.blazorFinishedLoading = true;
          readyBody();
        }, false);
      }
    }
    else {
      readyBody();
    }

  };

  const readyStateChange = () => {
    if (document.readyState === 'complete') {
      ready();
    }
  };

  // This is the one public interface
  // docReady(fn, context);
  // the context argument is optional - if present, it will be passed
  // as an argument to the callback

  if (typeof callback !== 'function') {
    throw new TypeError('callback for docReady(fn) must be a function');
  }
  // if ready has already fired, then just schedule the callback
  // to fire asynchronously, but right away
  if (readyFired) {
    setTimeout(function () {
      callback(context);
    }, 1);
    return;
  } else {
    // add the function and context to the list
    readyList.push({ fn: callback, ctx: context });
  }
  // if document already ready to go, schedule the ready function to run
  if (document.readyState === 'complete') {
    setTimeout(ready, 1);
  } else if (!readyEventHandlersInstalled) {
    // otherwise if we don't have event handlers installed, install them
    if (document.addEventListener) {
      // first choice is DOMContentLoaded event
      document.addEventListener('DOMContentLoaded', ready, false);
      // backup is window load event
      window.addEventListener('load', ready, false);
    }
    readyEventHandlersInstalled = true;
  }
};

export interface IEvent {
  type: string;
  event?: IEventItem;
  events?: Array<IEventItem>;
}

export interface IEventItem {
  action?: string;
  event?: string;
  callback?: Function;
}

interface IGlobalEventItem {
  type: string;
  action?: string;
  event?: string;
  callback: Set<Function>;
}

// Global event
export let globalEventList: Array<IGlobalEventItem> = [];

export const globalEvent = (event: IEvent) => {

  event.type.split(' ').forEach((type) => {
    if (event.event) {
      event.events = [...(event.events ?? []), ...[event.event]];
    }

    if (event.events) {
      for (let item of event.events) {
        let foundItem;

        if (item?.action) {
          let actionFound: IGlobalEventItem = globalFindAction(type, item?.action);
          if (actionFound) {
            foundItem = actionFound;
          }
        }

        if (item?.event) {
          let eventFound: IGlobalEventItem = globalFindEvent(type, item?.event);
          if (eventFound) {
            foundItem = eventFound;
          }
        }

        if (!item?.event && !item?.action) {
          // check global state.
          let globalFound: IGlobalEventItem = globalFind(type);
          if (globalFound) {
            foundItem = globalFound;
          }
        }

        foundItem ? foundItem.callback.add(item.callback) : globalEventAdd(type, item);
      }
    }

    document.body.removeEventListener(type, globalEventHandler);
    document.body.addEventListener(type, globalEventHandler);
  });
};

export const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const globalEventHandler = (e) => {
  const target: any = e.target;

  // check action
  let eventTarget = globalGetTargetAction(target);
  if (eventTarget) {
    const item: IGlobalEventItem = globalFindAction(e.type, eventTarget);
    if (item?.callback) {
      globalEventInvokeCallback(item?.callback, e);
    }
    return;
  }

  // check event
  eventTarget = globalGetTargetEvent(target);
  if (eventTarget) {
    const item: IGlobalEventItem = globalFindEvent(e.type, eventTarget);
    if (item?.callback) {
      globalEventInvokeCallback(item?.callback, e);
    }
    return;
  }

  // check event
  const item = globalMatchType(e.type);
  if (item?.callback) {
    globalEventInvokeCallback(item?.callback, e);
    return;
  }
};

const globalEventInvokeCallback = (cbs: Set<Function>, e: any) => {
  for (let item of cbs) {
    item?.call(null, e);
  }
};

const globalEventAdd = (type: string, item: IEventItem): void => {
  globalEventList.push({
    type: type,
    action: item?.action,
    event: item?.event,
    callback: new Set([item?.callback]),
  });
};

const globalFind = (itemType: string): IGlobalEventItem => {
  return globalEventList.find(({ type, action, event }) => type === itemType && !action && !event);
};

const globalFindEvent = (itemType: string, eventType: string): IGlobalEventItem => globalEventList.find(({ type, event }) => type === itemType && (event ?? '') === (eventType ?? ''));

const globalFindAction = (itemType: string, actionType: string): IGlobalEventItem => globalEventList.find(({ type, action }) => type === itemType && (action ?? '') === (actionType ?? ''));

const globalMatchType = (itemType: string): IGlobalEventItem => globalEventList.find(({ type, action, event }) => type === itemType && !action && !event);

const globalGetTargetAction = (target) => globalGetTarget(target, 'data-action');

const globalGetTargetEvent = (target) => globalGetTarget(target, 'data-event');

const globalGetTarget = (target, dataTarget: string) => {
  let eventTarget = target?.attributes[dataTarget]?.value;
  if (!eventTarget) eventTarget = target.parentNode?.attributes[dataTarget]?.value;
  return eventTarget;
};
